import { toast } from 'sonner'

import { useCallback, useRef, useState } from 'react'

import { AssistantModel, assistantError } from '@/http/models/assistant/Assistant'
import { AssistantLanguage } from '@/types/assistant'

import { assistantText } from './assistantText'

const MAX_RECORDING_MS = 120_000
const WAV_SAMPLE_RATE = 16_000

/**
 * Browsers record WebM/Opus, which Gemini does not list as an audio input; re-encode to 16 kHz mono 16-bit WAV
 * (about 32 KB a second). Falls back to the original clip if the browser cannot decode it.
 */
const toWav = async (blob: Blob): Promise<Blob> => {
  try {
    const decodeContext = new AudioContext()
    const decoded = await decodeContext.decodeAudioData(await blob.arrayBuffer())
    await decodeContext.close()
    const frames = Math.ceil(decoded.duration * WAV_SAMPLE_RATE)
    const offline = new OfflineAudioContext(1, frames, WAV_SAMPLE_RATE)
    const source = offline.createBufferSource()
    source.buffer = decoded
    source.connect(offline.destination)
    source.start()
    const samples = (await offline.startRendering()).getChannelData(0)

    const buffer = new ArrayBuffer(44 + samples.length * 2)
    const view = new DataView(buffer)
    const text = (offset: number, value: string) => [...value].forEach((c, i) => view.setUint8(offset + i, c.charCodeAt(0)))
    text(0, 'RIFF'); view.setUint32(4, 36 + samples.length * 2, true); text(8, 'WAVE')
    text(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true)
    view.setUint32(24, WAV_SAMPLE_RATE, true); view.setUint32(28, WAV_SAMPLE_RATE * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true)
    text(36, 'data'); view.setUint32(40, samples.length * 2, true)
    samples.forEach((sample, i) => {
      const clamped = Math.max(-1, Math.min(1, sample))
      view.setInt16(44 + i * 2, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true)
    })
    return new Blob([buffer], { type: 'audio/wav' })
  } catch (error) {
    console.warn('Voice: could not convert the recording to WAV; sending it as recorded', error)
    return blob
  }
}

/** Records from the microphone, sends the clip for transcription and hands back the text (audio is not kept). */
export const useVoiceInput = (language: AssistantLanguage, onText: (text: string) => void) => {
  const [state, setState] = useState<'idle' | 'recording' | 'transcribing'>('idle')
  const recorderRef = useRef<MediaRecorder | null>(null)
  const timerRef = useRef<number | null>(null)

  const stop = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
  }, [])

  const start = useCallback(async () => {
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (error) {
      console.warn('Microphone unavailable', error)
      toast.error(assistantText(language).micDenied)
      return
    }
    const mimeType = ['audio/webm', 'audio/ogg', 'audio/mp4'].find((t) => MediaRecorder.isTypeSupported(t))
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    const chunks: Blob[] = []
    recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data)
    recorder.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop())
      const blob = new Blob(chunks, { type: recorder.mimeType.split(';')[0] || 'audio/webm' })
      if (!blob.size) {
        setState('idle')
        return
      }
      setState('transcribing')
      const res = await AssistantModel.transcribe(await toWav(blob), language)
      setState('idle')
      if (res.success && res.data?.result.text) onText(res.data.result.text)
      else if (!res.success) toast.error(assistantError(res.error, assistantText(language).errorGeneric))
    }
    recorderRef.current = recorder
    recorder.start()
    setState('recording')
    timerRef.current = window.setTimeout(stop, MAX_RECORDING_MS)
  }, [language, onText, stop])

  const toggle = useCallback(() => (state === 'recording' ? stop() : state === 'idle' ? start() : undefined), [start, state, stop])

  return { state, toggle }
}
